import { fetchSvgText, packUrl } from '../content/svg.js';
import { ARC_POINTS, GAME_NAME, MARK_VIEWBOX, paw } from './brand.js';

/**
 * The name, set in the show's own letterforms.
 *
 * `brand.ts` draws the mark in the system face and is the fallback; this draws the same mark out of pack
 * art and replaces it once the art has landed. Two marks rather than one because they answer two different
 * requirements, and neither can be dropped: the title has to be on screen in the first frame, which rules
 * out anything fetched, and it should be in the show's hand, which cannot be anything else. So the drawn
 * one goes up immediately and this one takes over a moment later, on the same arc, at the same size, with
 * the same paw at each shoulder - the swap is meant to be unnoticeable except that the letters change.
 *
 * A pack with no `type/alphabet.svg`, or one whose alphabet is missing a letter the name needs, simply
 * never replaces anything. That is the same contract as every other piece of pack art: absence costs a
 * decoration, never a control.
 *
 * The layout is the work here. The sheet is 47 squares with no metrics of any kind - see
 * `scripts/build-alphabet.mjs`, which measures the ink in each and writes the boxes into the file - and
 * `textPath` is no help, because it sets text along a curve and these are shapes. So each glyph is placed
 * on the arc by hand: walked along by its own width, turned to the tangent where it lands, and dropped so
 * its foot sits on the curve.
 */

/** Where the sheet lives in the pack. One request, one precache entry. */
const ALPHABET = packUrl('type', 'alphabet');

/**
 * Space between two letters, as a fraction of the em.
 *
 * Small, because the glyphs carry their own: each one's measured box includes the gold plate around it,
 * and the plates are what should nearly touch.
 */
const TRACKING = 0.014;
/** A word space, same units. Wider than a normal face would set, because these letters are very wide. */
const WORD_SPACE = 0.14;
/** How much of the arc the name is allowed to fill, so the paws at the shoulders keep their room. */
const ARC_FILL = 0.96;
/**
 * The tallest a glyph may be drawn, in mark units.
 *
 * The band is 200 units deep and the arc's shoulders sit at 166 of it, so a letter much over 80 hangs off
 * the bottom of the box at the ends. This also stops a short name being blown up to fill the arc.
 */
const MAX_GLYPH_HEIGHT = 82;

interface Glyph {
  readonly node: SVGGElement;
  /** The glyph's own ink box within the em square: what makes this a wordmark and not a grid. */
  readonly ink: { x: number; y: number; width: number; height: number };
}

/**
 * The mark, or null if the pack cannot set this name.
 *
 * Null is the ordinary outcome for a pack without an alphabet, and the caller keeps whatever it already
 * has on screen.
 */
export async function packBrandMark(name = GAME_NAME): Promise<SVGSVGElement | null> {
  const sheet = await loadSheet();
  if (sheet === null) return null;

  const characters = [...name.toUpperCase()];
  // Every letter, or none: half a name in the show's hand and half in the system face is worse than
  // either, and it is the sort of thing that would ship unnoticed.
  const missing = characters.some((character) => character !== ' ' && !sheet.glyphs.has(character));
  if (missing) return null;

  return draw(characters, sheet);
}

interface Sheet {
  readonly em: number;
  readonly glyphs: Map<string, Glyph>;
  readonly defs: readonly SVGElement[];
}

async function loadSheet(): Promise<Sheet | null> {
  let text;
  try {
    text = await fetchSvgText(ALPHABET);
  } catch {
    // No alphabet in this pack. Expected, and the reason the drawn mark exists.
    return null;
  }

  const parsed = new DOMParser().parseFromString(text, 'image/svg+xml');
  if (parsed.querySelector('parsererror') !== null) return null;

  const em = Number(parsed.documentElement.getAttribute('data-em'));
  if (!Number.isFinite(em) || em <= 0) return null;

  const glyphs = new Map<string, Glyph>();
  for (const node of parsed.querySelectorAll('g[data-char]')) {
    const character = node.getAttribute('data-char') ?? '';
    const box = (node.getAttribute('data-ink') ?? '').trim().split(/\s+/).map(Number);
    if (character.length !== 1 || box.length !== 4 || box.some((value) => !Number.isFinite(value))) continue;
    const [x, y, width, height] = box as [number, number, number, number];
    if (width <= 0 || height <= 0) continue;
    glyphs.set(character, { node: node as SVGGElement, ink: { x, y, width, height } });
  }
  if (glyphs.size === 0) return null;

  /*
   * Anything the glyphs reference by id - only the ampersand has any, for the rectangle that splits its
   * face into two blues. It has to travel with them: a `clip-path` pointing at a missing `clipPath`
   * renders *nothing*, so leaving it behind would delete the character rather than flatten it.
   */
  const defs = [...parsed.querySelectorAll('defs > clipPath, defs > linearGradient, defs > mask')];
  return { em, glyphs, defs: defs as SVGElement[] };
}

function draw(characters: readonly string[], sheet: Sheet): SVGSVGElement {
  const set = characters.filter((character) => character !== ' ');
  const glyphs = set.map((character) => sheet.glyphs.get(character) as Glyph);

  /*
   * The foot of the row, in em units.
   *
   * The median rather than the lowest, because the alphabet is hand-lettered and one or two glyphs are
   * drawn larger than the rest - the `M` reaches 46 units below everything else. Aligning to the lowest
   * would lift the whole name off the arc to accommodate one letter; the median puts most of the row on
   * the curve and lets that one hang, which is how it is drawn.
   */
  const feet = glyphs.map((glyph) => glyph.ink.y + glyph.ink.height).sort((a, b) => a - b);
  const baseline = feet[Math.floor(feet.length / 2)] ?? sheet.em;

  const tracking = TRACKING * sheet.em;
  const space = WORD_SPACE * sheet.em;

  // The row's width in em units: every glyph's own width, a gap between neighbours, a space for a space.
  let width = 0;
  characters.forEach((character, index) => {
    if (index > 0) width += tracking;
    width += character === ' ' ? space : (sheet.glyphs.get(character) as Glyph).ink.width;
  });

  const arc = arcTable();
  const tallest = Math.max(...glyphs.map((glyph) => glyph.ink.height));
  const scale = Math.min((arc.length * ARC_FILL) / width, MAX_GLYPH_HEIGHT / tallest);

  // Centred on the arc, so the name sits over the middle of the tower whatever it is called.
  let cursor = (arc.length - width * scale) / 2;
  const parts: SVGElement[] = [];

  characters.forEach((character, index) => {
    if (index > 0) cursor += tracking * scale;
    if (character === ' ') {
      cursor += space * scale;
      return;
    }

    const glyph = sheet.glyphs.get(character) as Glyph;
    const advance = glyph.ink.width * scale;
    const at = arc.at(cursor + advance / 2);

    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttribute('href', `#${glyph.node.id}`);
    /*
     * Read right to left: take the glyph in em space, put its foot and its middle at the origin, size it,
     * turn it to the arc's tangent, and drop it where it belongs. Doing the centring inside the scale is
     * what lets one transform carry the whole placement.
     */
    use.setAttribute(
      'transform',
      `translate(${round(at.x)} ${round(at.y)}) rotate(${round(at.angle)}) scale(${round(scale, 4)})` +
        ` translate(${round(-(glyph.ink.x + glyph.ink.width / 2))} ${round(-baseline)})`,
    );
    parts.push(use);
    cursor += advance;
  });

  return assemble(sheet, parts);
}

/** The finished mark: the glyph library, the two paws, and the row. */
function assemble(sheet: Sheet, parts: readonly SVGElement[]): SVGSVGElement {
  const shell = new DOMParser().parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${MARK_VIEWBOX}"` +
      ` class="brand-mark brand-mark--pack" aria-hidden="true" data-brand="title">` +
      `<defs/>` +
      paw('translate(48 148) rotate(-16) scale(0.92)') +
      paw('translate(952 148) rotate(16) scale(0.92)') +
      `</svg>`,
    'image/svg+xml',
  ).documentElement as unknown as SVGSVGElement;

  const defs = shell.querySelector('defs') as SVGDefsElement;
  for (const extra of sheet.defs) defs.append(extra.cloneNode(true));
  // Only the glyphs this name sets, so the mark carries no letters it does not use.
  const used = new Set(parts.map((part) => part.getAttribute('href')));
  for (const glyph of sheet.glyphs.values()) {
    if (used.has(`#${glyph.node.id}`)) defs.append(glyph.node.cloneNode(true));
  }
  for (const part of parts) shell.append(part);
  return shell;
}

/**
 * The arc, as a length-to-point table.
 *
 * One quadratic, sampled evenly in `t` and walked to build a running length, which is what turns "so many
 * units along the curve" into a point and a direction. Sampling rather than solving because the answer
 * only has to be right to less than a pixel, and a closed form for the arc length of a quadratic is a
 * page of algebra that would still need inverting.
 */
function arcTable() {
  const { from, control, to } = ARC_POINTS;
  const STEPS = 240;

  const point = (t: number) => ({
    x: (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * control.x + t * t * to.x,
    y: (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * control.y + t * t * to.y,
  });
  const tangent = (t: number) => ({
    x: 2 * (1 - t) * (control.x - from.x) + 2 * t * (to.x - control.x),
    y: 2 * (1 - t) * (control.y - from.y) + 2 * t * (to.y - control.y),
  });

  const lengths = [0];
  let previous = point(0);
  for (let i = 1; i <= STEPS; i++) {
    const next = point(i / STEPS);
    lengths.push((lengths[i - 1] as number) + Math.hypot(next.x - previous.x, next.y - previous.y));
    previous = next;
  }
  const length = lengths[STEPS] as number;

  return {
    length,
    /** The point and tangent angle at a distance along the curve. */
    at(distance: number) {
      const wanted = Math.max(0, Math.min(length, distance));
      let i = 1;
      while (i < STEPS && (lengths[i] as number) < wanted) i++;
      const before = lengths[i - 1] as number;
      const after = lengths[i] as number;
      const within = after === before ? 0 : (wanted - before) / (after - before);
      const t = (i - 1 + within) / STEPS;

      const on = point(t);
      const direction = tangent(t);
      return { x: on.x, y: on.y, angle: (Math.atan2(direction.y, direction.x) * 180) / Math.PI };
    },
  };
}

function round(value: number, places = 2): number {
  return Number(value.toFixed(places));
}
